/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import type { LearnerProfile_ExternalLinks as ExternalLinks } from '__generated__/graphql-types';

import { Button, Dialog, Divider, Grid, InlineNotification, TextField, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import type { LearnerProfile as Profile } from 'bundles/account-profile/common/types';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import ExternalLinkInputs from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/ExternalLinkInputs';
import ProfileResumeUploader from 'bundles/account-profile/components/recruiting-profile-viewer/components/additional-info/ProfileResumeUploader';
import useDeleteResume from 'bundles/account-profile/components/recruiting-profile-viewer/queries/hooks/useDeleteResume';
import {
  isUploadedResume,
  isValidResumeUrlSource,
  isValidUrl,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/additionalInfoUtils';
import {
  validateAll,
  validateField,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/validationUtils';
import type {
  ValidationErrors,
  ValidationRules,
} from 'bundles/account-profile/components/recruiting-profile-viewer/utils/validationUtils';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { shouldEnableResumeUpload } from 'bundles/employability-common/feature-flags/learnerProfileEpicFeatureFlags';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

type RequiredFields = 'form' | 'about' | 'externalLinks' | 'resumeUrl';

const styles = {
  formContainer: css`
    // Remove inherited styles that were conflicting with cds
    input {
      margin-bottom: 0;
      border: 0;
      box-shadow: none !important;
      background: transparent;
    }
  `,
  divider: (theme: Theme) => css`
    margin-top: ${theme.spacing(24)};
  `,
  formInput: css`
    label {
      margin-bottom: 0;
    }
  `,
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const validationRules = (): ValidationRules<Partial<Profile>, keyof Profile> => [
  {
    field: 'about',
    getValidationErrors: (value?: string) => {
      if (!value) return ''; // This field is optional, so we don't need to validate if the field is empty

      if (value.length > 500) {
        return _t('You have exceeded the maximum number of characters');
      }

      return '';
    },
  },
  {
    field: 'resumeUrl',
    getValidationErrors: (value?: string) => {
      if (!value) return ''; // This field is optional, so we don't need to validate if the field is empty

      if (!isValidUrl(value)) {
        return _t('Please enter a valid URL');
      }
      if (!isValidResumeUrlSource(value)) {
        return _t('File must be hosted on Google Drive or One Drive');
      }

      return '';
    },
  },
  {
    field: 'externalLinks',
    getValidationErrors: (value?: ExternalLinks) => {
      if (!value) return ''; // This field is optional, so we don't need to validate if the field is empty
      const invalidUrls = Object.values(value).reduce((acc, item) => {
        // When we destructure the External Link, we get ALL the values, including the typename. The typename isn't a valid url and will be added to invalidUrls.
        // We need to ignore the typename, so we don't have any false positives
        if (!item || isValidUrl(item) || item === 'LearnerProfile_ExternalLinks') return acc;
        return [...acc, item];
      }, []);
      if (invalidUrls.length > 0) {
        return _t('Please ensure all URLs have been entered correctly');
      }
      return '';
    },
  },
];

const EditAdditionalInfoDialog = ({ isOpen, onClose }: Props) => {
  const { profileState, updateLearnerProfile, restrictHiringSolutions } = useContext(ProfileContext);
  const { HeadingGroup, Content, Actions } = Dialog;
  const [about, setAbout] = useState(profileState?.about);
  const [resumeUrl, setResumeUrl] = useState(profileState?.resumeUrl);
  const [externalLinks, setExternalLinks] = useState(profileState?.externalLinks);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const { deleteResume } = useDeleteResume();

  const lazyResumeCleanup = (url: string) => {
    deleteResume(url)
      .then((result) => {
        if (!result) {
          throw new Error('resume_cleanup_failed');
        }
      })
      .catch((e) => {
        Sentry.captureException(e);
      });
  };

  const formFieldRefs = {
    about: useRef<HTMLDivElement | null>(null),
    externalLinks: useRef<HTMLFieldSetElement | null>(null),
    form: useRef<HTMLDivElement | null>(null),
    resumeUrl: useRef<HTMLDivElement | null>(null),
  };

  const scrollToFirstError = (validationErrors: ValidationErrors) => {
    const firstError = Object.keys(validationErrors)[0];
    const firstErrorRef = formFieldRefs[firstError as RequiredFields];
    firstErrorRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  };

  const handleResumeChange = (resume?: { url?: string }) => {
    // multiple transient resumes have been uploaded, deleted the preceding one
    if (resumeUrl && resume?.url !== resumeUrl && profileState?.resumeUrl !== resumeUrl) {
      lazyResumeCleanup(resumeUrl);
    }
    setResumeUrl(resume?.url);
  };

  const handleClose = () => {
    // Reset form state
    setFormErrors({});
    setAbout(profileState?.about);
    setExternalLinks(profileState?.externalLinks);

    // if the user has uploaded a resume but has not submitted the form, delete it
    if (resumeUrl && resumeUrl !== profileState?.resumeUrl) {
      lazyResumeCleanup(resumeUrl);
    }

    setResumeUrl(profileState?.resumeUrl);
    onClose();
  };

  const validateTextField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event?.target;
    const validationMessage = validateField(validationRules, name as keyof Profile, value);
    const errors: ValidationErrors = {
      ...formErrors,
      [name]: validationMessage,
    };
    setFormErrors(errors);
  };

  const validateExternalLinks = (links: ExternalLinks) => {
    const validationMessage = validateField(validationRules, 'externalLinks' as keyof Profile, links);
    const errors: ValidationErrors = {
      ...formErrors,
      externalLinks: validationMessage,
    };
    setFormErrors(errors);
  };

  const handleSubmit = async () => {
    setIsSaving(true);

    const profileUpdates: Partial<Profile> = {
      about,
      externalLinks,
      resumeUrl,
    };

    const validationErrors = validateAll(validationRules, profileUpdates);
    setFormErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      scrollToFirstError(validationErrors);
      setIsSaving(false);
      return;
    }

    try {
      const cleanupResume = () => {
        // resume was cleared, or it was set to a new file
        if (
          isUploadedResume(profileState?.resumeUrl) &&
          (!resumeUrl || (resumeUrl && resumeUrl !== profileState?.resumeUrl))
        ) {
          lazyResumeCleanup(profileState?.resumeUrl);
        }
      };

      await updateLearnerProfile(profileUpdates);

      cleanupResume();

      setIsSaving(false);
      onClose();
    } catch (error) {
      setIsSaving(false);
      setFormErrors((prevState) => ({
        ...prevState,
        form: _t('There was an error saving the form, please try again'),
      }));
      scrollToFirstError({ ...formErrors, form: _t('There was an error saving the form, please try again') });
    }
  };

  const getSubheadingText = () => {
    return restrictHiringSolutions
      ? _t('Help viewers of your profile get to know you better by adding links and a short summary about yourself.')
      : _t(
          'Help employers get to know you better by adding other links and describing what makes you a great candidate.'
        );
  };

  const getAboutYouDescriptionText = () => {
    return restrictHiringSolutions
      ? _t('Add a short summary about yourself.')
      : _t('A personal summary can help recruiters understand your unique strengths.');
  };

  const getAboutYouPlaceholderText = () => {
    return restrictHiringSolutions
      ? _t(
          'Include a few brief details about yourself. For example, what are you most passionate about? What achievements are you most proud of?'
        )
      : _t(
          'Include a few brief details about yourself. For example, what are you most passionate about? What achievements are you most proud of? What sets you apart from other candidates?'
        );
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} width="medium" variant="standard" aria-describedby="dialog-content">
      <HeadingGroup>{_t('Additional info')}</HeadingGroup>
      <Content id="dialog-content">
        <TrackedDiv
          trackClicks={false}
          withVisibilityTracking
          // not tracked in eventing v3
          trackingName="profile_additional_info_dialog"
          requireFullyVisible={false}
        >
          <Grid css={styles.formContainer} container spacing={24}>
            <Grid item xs={12}>
              <Typography2 component="p">{getSubheadingText()}</Typography2>
            </Grid>
            {!restrictHiringSolutions && (
              <Grid item xs={12}>
                {shouldEnableResumeUpload() ? (
                  <ProfileResumeUploader onChange={handleResumeChange} resumeUrl={resumeUrl} profile={profileState} />
                ) : (
                  <TextField
                    fullWidth
                    label={_t('Resume')}
                    name="resumeUrl"
                    placeholder={_t('Enter a link to your resume (Google Drive or One Drive only)')}
                    supportText={_t('Have a resume ready? Add it here to make it easy for employers to view it.')}
                    value={resumeUrl || ''}
                    onChange={(e) => {
                      setResumeUrl(e.target.value);
                      validateTextField(e);
                    }}
                    inputProps={{
                      className: 'fs-mask',
                    }}
                    validationStatus={formErrors.resumeUrl ? 'error' : undefined}
                    validationLabel={formErrors.resumeUrl}
                    css={styles.formInput}
                    ref={formFieldRefs.resumeUrl}
                  />
                )}
              </Grid>
            )}
            <Grid item xs={12}>
              <ExternalLinkInputs
                values={externalLinks || {}}
                onUpdate={(links) => {
                  setExternalLinks(links);
                  validateExternalLinks(links);
                }}
                validationStatus={formErrors.externalLinks ? 'error' : undefined}
                validationLabel={formErrors.externalLinks}
                ref={formFieldRefs.externalLinks}
              />
              <Divider css={styles.divider} color="dark" />
            </Grid>
            <Grid item xs={12}>
              <TextField
                ref={formFieldRefs.about}
                multiline
                label={_t('About you')}
                supportText={getAboutYouDescriptionText()}
                name="about"
                characterLimit={500}
                fullWidth
                value={about || ''}
                onChange={(e) => {
                  setAbout(e.target.value);
                  validateTextField(e);
                }}
                type="text"
                validationStatus={formErrors.about ? 'error' : undefined}
                validationLabel={formErrors.about}
                placeholder={getAboutYouPlaceholderText()}
                css={styles.formInput}
                inputProps={{ required: false }}
              />
            </Grid>
          </Grid>
          {formErrors.form && (
            <InlineNotification ref={formFieldRefs.form} severity="error">
              {formErrors.form}
            </InlineNotification>
          )}
        </TrackedDiv>
      </Content>
      <Actions>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSaving}
          component={TrackedButton}
          // not tracked in eventing v3
          trackingName="profile_additional_info_save"
          requireFullyVisible={false}
          withVisibilityTracking={true}
          loading={isSaving}
        >
          {isSaving ? _t('Saving...') : _t('Save')}
        </Button>
      </Actions>
    </Dialog>
  );
};

const ErrorBoundEditAdditionalInfoDialog = (props: Props) => (
  <ErrorBoundaryWithTagging
    componentName="EditAdditionalInfoDialog"
    errorComponent={
      <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
        {_t('There has been an error in the edit additional info dialog. Our development team will be alerted.')}
      </InlineNotification>
    }
  >
    <EditAdditionalInfoDialog {...props} />
  </ErrorBoundaryWithTagging>
);

export default ErrorBoundEditAdditionalInfoDialog;
